import axios from "axios";
import {
    getAuth,
    sendSignInLinkToEmail,
    createUserWithEmailAndPassword,
} from "firebase/auth";
import firebase from "@/plugins/firebase";

const firebaseUrl = "https://wade-o-lution-default-rtdb.firebaseio.com";
// In a Vue app, you might use VUE_APP_FB_KEY or another mechanism to expose env variables:
const firebaseApiKey = "AIzaSyCldSUm0wvjBKIJdOVI7tvoA3t2LTmCsUE"; // process.env.FB_KEY;

export const fetchObjects = async(objectType) => {
    try {
        const response = await axios.get(
            `${firebaseUrl}/${objectType}.json?auth=${firebaseApiKey}`
        );
        let items =
            objectType === "people" && response.data ?
            response.data.filter((x) => x && x.name) :
            response.data;
        if (!items) {
            return [];
        }
        const result = Object.keys(items).map((id) => ({
            key: id,
            ...items[id],
        }));
        return result.filter((i) => !!i);
    } catch (error) {
        console.error(error);
        return error;
    }
};

export const fetchSingleObjectByKey = async(objectType, objectKey) => {
    try {
        const response = await axios.get(
            `${firebaseUrl}/${objectType}/${objectKey}.json?auth=${firebaseApiKey}`
        );
        return response.data;
    } catch (error) {
        console.error(error);
        return error;
    }
};

const checkIfObjectExists = async(objectType, objectId) => {
    try {
        const response = await axios.get(
            `${firebaseUrl}/${objectType}/${objectId}.json?auth=${firebaseApiKey}`
        );
        return response.data !== null;
    } catch (error) {
        console.error(
            `Error checking ${objectType} ${objectId}:`,
            error.response ? error.response.data : error.message
        );
        return false;
    }
};

const findKey = async(objectType) => {
    try {
        const data = await fetchObjects(objectType);
        // Check if data exists and has length
        if (!data || !data.length) {
            return 1;
        }
        const key = parseInt(data[data.length - 1].key, 10) + 1;
        return isNaN(key) ? 1 : key;
    } catch (error) {
        console.error(error);
        return error;
    }
};

export const getCompanyLocationsByUser = async(userId) => {
    try {
        // Fetch all company objects
        const companyResponse = await axios.get(
            `${firebaseUrl}/company.json?auth=${firebaseApiKey}`
        );
        const companies = companyResponse.data;

        // Filter companies by userId found in the "users" array
        const userCompanies = Object.keys(companies)
            .map((id) => ({ key: id, ...companies[id] }))
            .filter((company) => company.users && company.users.includes(userId));

        if (userCompanies.length === 0) {
            console.log(`No companies found for user: ${userId}`);
            return [];
        }

        // Fetch all location objects
        const locationResponse = await axios.get(
            `${firebaseUrl}/locations.json?auth=${firebaseApiKey}`
        );
        const locations = locationResponse.data;

        // Filter locations by companyId
        const userLocations = Object.keys(locations)
            .map((id) => ({ key: id, ...locations[id] }))
            .filter((location) =>
                userCompanies.some((company) => company.key === location.companyId)
            );

        console.log(`Successfully fetched objects for user: ${userId}`);
        return { company: userCompanies, locations: userLocations };
    } catch (error) {
        console.error(error);
        return error;
    }
};

export const postObject = async(object, objectType, index) => {
    const objectNumber = index ? index : await findKey(objectType);
    const exists = await checkIfObjectExists(objectType, objectNumber);
    if (exists) {
        return;
    }
    try {
        const response = await axios.put(
            `${firebaseUrl}/${objectType}/${objectNumber}.json?auth=${firebaseApiKey}`, { key: objectNumber, ...object }
        );
        return { key: objectNumber, ...response.data };
    } catch (error) {
        console.error(
            `Error posting ${objectType} ${objectNumber}:`,
            error.response ? error.response.data : error.message
        );
    }
};

export const deleteObj = async(objectKey, objectType) => {
    try {
        const response = await axios.delete(
            `${firebaseUrl}/${objectType}/${objectKey}.json?auth=${firebaseApiKey}`
        );
        return response.data;
    } catch (error) {
        console.error(
            `Error deleting ${objectType} ${objectKey}:`,
            error.response ? error.response.data : error.message
        );
    }
};

export const updateSingleObjectToFirebase = async(
    object,
    objectType,
    objectKey
) => {
    try {
        const response = await axios.put(
            `${firebaseUrl}/${objectType}/${objectKey}.json?auth=${firebaseApiKey}`,
            object
        );
        return response.data;
    } catch (error) {
        console.error(
            `Error updating ${objectType} ${objectKey}:`,
            error.response ? error.response.data : error.message
        );
    }
};

export const postSingleObjectToFirebase = async(
    object,
    objectType,
    objectKey
) => {
    const exists = await checkIfObjectExists(objectType, objectKey);
    if (exists) {
        console.log(`${objectType} ${objectKey} already exists. Skipping...`);
        return;
    }
    try {
        const response = await axios.put(
            `${firebaseUrl}/${objectType}/${objectKey}.json?key=${firebaseApiKey}`,
            object
        );
        console.log(
            `${objectType} ${objectKey} successfully posted:`,
            response.data
        );
        return response.data;
    } catch (error) {
        console.error(
            `Error posting ${objectType} ${objectKey}:`,
            error.response ? error.response.data : error.message
        );
    }
};

const chunkArray = (array, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
};

export const postObjectsToFirebase = async(
    objects,
    objectType,
    chunkSize = 20
) => {
    const chunks = chunkArray(objects, chunkSize);
    for (const chunk of chunks) {
        // Process each chunk sequentially
        for (let i = 0; i < chunk.length; i++) {
            await postSingleObjectToFirebase(chunk[i], objectType, i);
        }
    }
    console.log("All objects processed.");
};

export const getLocationsByUser = async(userId) => {
    try {
        const users = await fetchObjects("users");
        const user = users.filter((user) => user.uid === userId)[0];

        if (!user) {
            return [];
        }

        const companyId = user.companyId;
        if (!companyId) {
            return [];
        }

        const locations = await fetchObjects("location");
        const result = locations.filter(
            (location) => location.companyId == companyId
        );

        return result;
    } catch (error) {
        console.error("getLocationsByUser error:", error);
        return [];
    }
};

export const sendInviteEmail = async(email, companyId) => {
    console.log("Sending invite email to:", email, "for company:", companyId);
    try {
        const auth = getAuth();

        // Create user in Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(
            auth,
            email,
            generateTemporaryPassword()
        );
        const userId = userCredential.user.uid;

        // Create initial user object in Realtime DB
        const userData = {
            email: email,
            companyId: companyId,
            createdAt: new Date().toISOString(),
            firstName: "",
            lastName: "",
            ...userCredential.user,
        };

        await postSingleObjectToFirebase(userData, "users", userId);

        // Update company's users array
        const company = await fetchSingleObjectByKey("company", companyId);
        if (company) {
            const updatedUsers = [...(company.users || []), userId];
            await updateSingleObjectToFirebase({...company, users: updatedUsers },
                "company",
                companyId
            );
        }

        // Send password reset email
        const actionCodeSettings = {
            url: `${
        window.location.origin
      }/onboard?companyId=${companyId}&email=${encodeURIComponent(
        email
      )}&uid=${userId}`,
            handleCodeInApp: true,
        };

        await sendSignInLinkToEmail(auth, email, actionCodeSettings);
        return { success: true, message: "Invitation sent successfully" };
    } catch (error) {
        console.error("Error sending invite:", error);
        return { success: false, message: error.message };
    }
};

// Helper function to generate a temporary password
const generateTemporaryPassword = () => {
    return (
        Math.random().toString(36).slice(-12) +
        Math.random().toString(36).slice(-12) +
        Math.random().toString(36).slice(-12)
    );
};

// Dashboard-specific Firebase helpers
export const createUserDashboard = async(userId, dashboardData) => {
    try {
        const dashboardId = `dashboard_${userId}_${Date.now()}`;
        const dashboard = {
            userId,
            dashboardId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            ...dashboardData,
        };

        const response = await axios.put(
            `${firebaseUrl}/user_dashboards/${dashboardId}.json?auth=${firebaseApiKey}`,
            dashboard
        );

        return { dashboardId, ...response.data };
    } catch (error) {
        console.error("Error creating dashboard:", error);
        throw error;
    }
};

export const getUserDashboards = async(userId) => {
    try {
        const currentUserId = userId || localStorage.getItem("uid") || "anonymous";

        const response = await axios.get(
            `${firebaseUrl}/user_dashboards.json?auth=${firebaseApiKey}`
        );

        if (!response.data) {
            const defaultDashboard = await createUserDashboard(
                currentUserId,
                getDefaultDashboard()
            );
            return [defaultDashboard];
        }

        const dashboards = Object.keys(response.data)
            .map((key) => ({
                key,
                ...response.data[key],
            }))
            .filter((dashboard) => dashboard.userId === currentUserId)
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

        if (dashboards.length > 0) {
            return dashboards;
        } else {
            const defaultDashboard = await createUserDashboard(
                currentUserId,
                getDefaultDashboard()
            );
            return [defaultDashboard];
        }
    } catch (error) {
        console.error("Error fetching user dashboards:", error);
        const fallbackUserId = userId || localStorage.getItem("uid") || "anonymous";
        const fallbackDashboard = {
            dashboardId: `fallback_${fallbackUserId}_${Date.now()}`,
            userId: fallbackUserId,
            ...getDefaultDashboard(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        return [fallbackDashboard];
    }
};

export const getUserDashboard = async(userId, dashboardId = null) => {
    try {
        const dashboards = await getUserDashboards(userId);

        if (dashboardId) {
            // Return specific dashboard
            const dashboard = dashboards.find((d) => d.dashboardId === dashboardId);
            return dashboard || dashboards[0]; // Fallback to first dashboard
        }

        // Return most recent dashboard
        return dashboards[0];
    } catch (error) {
        console.error("Error fetching user dashboard:", error);
        throw error;
    }
};

export const updateDashboard = async(dashboardId, updates) => {
    try {
        const updatedData = {
            ...updates,
            updatedAt: new Date().toISOString(),
        };

        const response = await axios.patch(
            `${firebaseUrl}/user_dashboards/${dashboardId}.json?auth=${firebaseApiKey}`,
            updatedData
        );

        return response.data;
    } catch (error) {
        console.error("Error updating dashboard:", error);
        throw error;
    }
};

export const deleteDashboard = async(dashboardId) => {
    try {
        const response = await axios.delete(
            `${firebaseUrl}/user_dashboards/${dashboardId}.json?auth=${firebaseApiKey}`
        );

        return response.data;
    } catch (error) {
        console.error("Error deleting dashboard:", error);
        throw error;
    }
};

export const duplicateDashboard = async(dashboardId, newName) => {
    try {
        // Get the original dashboard
        const response = await axios.get(
            `${firebaseUrl}/user_dashboards/${dashboardId}.json?auth=${firebaseApiKey}`
        );

        if (!response.data) {
            throw new Error("Dashboard not found");
        }

        const originalDashboard = response.data;

        // Create new dashboard with copied data
        const newDashboard = {
            ...originalDashboard,
            name: newName || `${originalDashboard.name} (Copy)`,
            description: originalDashboard.description ?
                `${originalDashboard.description} (Copy)` : "",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };

        // Remove the old dashboardId and let createUserDashboard generate a new one
        delete newDashboard.dashboardId;

        return await createUserDashboard(originalDashboard.userId, newDashboard);
    } catch (error) {
        console.error("Error duplicating dashboard:", error);
        throw error;
    }
};

// Helper function to create default dashboard
function getDefaultDashboard() {
    return {
        name: "My IoT Dashboard",
        description: "Start building your custom IoT monitoring dashboard",
        layout: {
            columns: 12,
            gap: 16,
        },
        widgets: [], // Start with no widgets - blank dashboard
    };
}

export const getWidgetTemplates = async() => {
    try {
        const response = await axios.get(
            `${firebaseUrl}/widget_templates.json?auth=${firebaseApiKey}`
        );

        if (!response.data) {
            return [];
        }

        return Object.keys(response.data).map((key) => ({
            key,
            ...response.data[key],
        }));
    } catch (error) {
        console.warn("Error fetching widget templates:", error);
        return []; // Return empty array instead of throwing
    }
};

export const initializeWidgetTemplates = async() => {
    try {
        // Check if templates already exist
        const response = await axios.get(
            `${firebaseUrl}/widget_templates.json?auth=${firebaseApiKey}`
        );

        if (response.data && Object.keys(response.data).length > 0) {
            return; // Templates already exist
        }

        // Create new IoT/Device focused templates
        const defaultTemplates = [
            // Metrics - New IoT/Device focused widgets
            {
                id: "metric_alerts_over_time",
                type: "chart",
                name: "Alerts Over Time",
                description: "Time series chart showing alert trends",
                category: "alerts",
                defaultSize: { width: 6, height: 4 },
                icon: "mdi-chart-timeline-variant",
                configSchema: {
                    title: { type: "string", default: "Alerts Over Time" },
                    chartType: { type: "string", default: "line" },
                    timeRange: { type: "string", default: "30d" },
                },
                dataEndpoint: "/api/charts/alerts-timeline",
            },
            {
                id: "metric_alerts_by_location",
                type: "chart",
                name: "Alerts by Location",
                description: "Bar chart showing alerts grouped by location",
                category: "alerts",
                defaultSize: { width: 6, height: 4 },
                icon: "mdi-map-marker-alert",
                configSchema: {
                    title: { type: "string", default: "Alerts by Location" },
                    chartType: { type: "string", default: "bar" },
                },
                dataEndpoint: "/api/charts/alerts-by-location",
            },
            {
                id: "metric_gyro_accelerometer",
                type: "chart",
                name: "Gyroscope Data",
                description: "Real-time gyroscope data visualization",
                category: "sensors",
                defaultSize: { width: 8, height: 4 },
                icon: "mdi-axis-arrow",
                configSchema: {
                    title: { type: "string", default: "Gyroscope Data" },
                    chartType: { type: "string", default: "line" },
                    sensors: { type: "array", default: ["gyro"] },
                },
                dataEndpoint: "/api/charts/gyro-data",
            },
            {
                id: "metric_accelerometer_data",
                type: "chart",
                name: "Accelerometer Data",
                description: "Real-time accelerometer data visualization",
                category: "sensors",
                defaultSize: { width: 8, height: 4 },
                icon: "mdi-axis-arrow",
                configSchema: {
                    title: { type: "string", default: "Accelerometer Data" },
                    chartType: { type: "string", default: "line" },
                },
                dataEndpoint: "/api/charts/accelerometer-data",
            },
            {
                id: "metric_battery_levels",
                type: "chart",
                name: "Battery Level by Device",
                description: "Battery status across all devices",
                category: "devices",
                defaultSize: { width: 6, height: 3 },
                icon: "mdi-battery",
                configSchema: {
                    title: { type: "string", default: "Device Battery Levels" },
                    chartType: { type: "string", default: "bar" },
                },
                dataEndpoint: "/api/charts/battery-levels",
            },
            {
                id: "metric_device_status",
                type: "chart",
                name: "Device Status",
                description: "Donut chart showing device status distribution",
                category: "devices",
                defaultSize: { width: 4, height: 3 },
                icon: "mdi-wifi",
                configSchema: {
                    title: { type: "string", default: "Device Status" },
                    chartType: { type: "string", default: "doughnut" },
                },
                dataEndpoint: "/api/charts/device-status",
            },
            {
                id: "metric_work_orders",
                type: "chart",
                name: "Work Orders Created Over Time",
                description: "Timeline of work order creation",
                category: "maintenance",
                defaultSize: { width: 6, height: 4 },
                icon: "mdi-clipboard-list",
                configSchema: {
                    title: { type: "string", default: "Work Orders Over Time" },
                    chartType: { type: "string", default: "line" },
                    timeRange: { type: "string", default: "30d" },
                },
                dataEndpoint: "/api/charts/work-orders-timeline",
            },
            // Additional Recommended Widgets
            {
                id: "metric_device_health_score",
                type: "metric",
                name: "Device Health Score",
                description: "Overall health score across all devices",
                category: "devices",
                defaultSize: { width: 3, height: 2 },
                icon: "mdi-heart-pulse",
                configSchema: {
                    title: { type: "string", default: "Device Health" },
                    color: { type: "string", default: "#4CAF50" },
                },
                dataEndpoint: "/api/metrics/device-health",
            },
            {
                id: "metric_critical_alerts",
                type: "metric",
                name: "Critical Alerts Count",
                description: "Number of critical alerts requiring attention",
                category: "alerts",
                defaultSize: { width: 3, height: 2 },
                icon: "mdi-alert-octagon",
                configSchema: {
                    title: { type: "string", default: "Critical Alerts" },
                    color: { type: "string", default: "#f44336" },
                },
                dataEndpoint: "/api/metrics/critical-alerts",
            },
            {
                id: "metric_temperature_trends",
                type: "chart",
                name: "Temperature Trends",
                description: "Temperature monitoring across devices",
                category: "sensors",
                defaultSize: { width: 6, height: 3 },
                icon: "mdi-thermometer",
                configSchema: {
                    title: { type: "string", default: "Temperature Trends" },
                    chartType: { type: "string", default: "line" },
                },
                dataEndpoint: "/api/charts/temperature-trends",
            },
            {
                id: "metric_maintenance_schedule",
                type: "table",
                name: "Upcoming Maintenance",
                description: "Scheduled maintenance and inspections",
                category: "maintenance",
                defaultSize: { width: 8, height: 4 },
                icon: "mdi-calendar-clock",
                configSchema: {
                    title: { type: "string", default: "Maintenance Schedule" },
                    limit: { type: "number", default: 10 },
                },
                dataEndpoint: "/api/tables/maintenance-schedule",
            },
            {
                id: "metric_torque_deviation",
                type: "chart",
                name: "Torque Deviation Analysis",
                description: "Analysis of torque deviations over time",
                category: "sensors",
                defaultSize: { width: 6, height: 4 },
                icon: "mdi-wrench",
                configSchema: {
                    title: { type: "string", default: "Torque Analysis" },
                    chartType: { type: "string", default: "scatter" },
                },
                dataEndpoint: "/api/charts/torque-deviation",
            },
            {
                id: "metric_response_time",
                type: "metric",
                name: "Average Response Time",
                description: "Average time to respond to critical alerts",
                category: "maintenance",
                defaultSize: { width: 3, height: 2 },
                icon: "mdi-clock-fast",
                configSchema: {
                    title: { type: "string", default: "Response Time" },
                    color: { type: "string", default: "#FF9800" },
                },
                dataEndpoint: "/api/metrics/response-time",
            },
            {
                id: "metric_asset_lifecycle",
                type: "table",
                name: "Asset Lifecycle",
                description: "Device age, warranty status, and replacement planning",
                category: "devices",
                defaultSize: { width: 8, height: 4 },
                icon: "mdi-timeline-clock",
                configSchema: {
                    title: { type: "string", default: "Asset Lifecycle" },
                    limit: { type: "number", default: 10 },
                },
                dataEndpoint: "/api/tables/asset-lifecycle",
            },
        ];

        // Save templates to Firebase
        for (const template of defaultTemplates) {
            await axios.put(
                `${firebaseUrl}/widget_templates/${template.id}.json?auth=${firebaseApiKey}`,
                template
            );
        }

        console.log("Widget templates initialized successfully");
    } catch (error) {
        console.warn("Failed to initialize widget templates:", error);
        // Don't throw error - let the app continue with fallback templates
    }
};

// Default widget templates
const getDefaultWidgetTemplates = () => [
    // Metrics
    {
        id: "metric_total_users",
        type: "metric",
        name: "Total Users",
        description: "Display total number of users",
        category: "users",
        defaultSize: { width: 4, height: 2 },
        icon: "mdi-account-group",
        configSchema: {
            title: { type: "string", default: "Total Users" },
            color: { type: "string", default: "#2196F3" },
        },
        dataEndpoint: "/api/metrics/users",
    },
    {
        id: "metric_revenue",
        type: "metric",
        name: "Revenue",
        description: "Display total revenue",
        category: "sales",
        defaultSize: { width: 4, height: 2 },
        icon: "mdi-currency-usd",
        configSchema: {
            title: { type: "string", default: "Revenue" },
            color: { type: "string", default: "#4CAF50" },
        },
        dataEndpoint: "/api/metrics/revenue",
    },
    {
        id: "metric_conversion_rate",
        type: "metric",
        name: "Conversion Rate",
        description: "Display conversion rate percentage",
        category: "sales",
        defaultSize: { width: 4, height: 2 },
        icon: "mdi-trending-up",
        configSchema: {
            title: { type: "string", default: "Conversion Rate" },
            color: { type: "string", default: "#FF9800" },
        },
        dataEndpoint: "/api/metrics/conversion",
    },
    // Charts
    {
        id: "chart_sales_over_time",
        type: "chart",
        name: "Sales Over Time",
        description: "Line chart showing sales trends",
        category: "sales",
        defaultSize: { width: 6, height: 4 },
        icon: "mdi-chart-line",
        configSchema: {
            title: { type: "string", default: "Sales Over Time" },
            chartType: { type: "string", default: "line" },
            timeRange: { type: "string", default: "30d" },
        },
        dataEndpoint: "/api/charts/sales-timeline",
    },
    {
        id: "chart_user_growth",
        type: "chart",
        name: "User Growth",
        description: "Area chart showing user growth",
        category: "users",
        defaultSize: { width: 6, height: 4 },
        icon: "mdi-chart-areaspline",
        configSchema: {
            title: { type: "string", default: "User Growth" },
            chartType: { type: "string", default: "area" },
            timeRange: { type: "string", default: "30d" },
        },
        dataEndpoint: "/api/charts/user-growth",
    },
    {
        id: "chart_top_products",
        type: "chart",
        name: "Top Products",
        description: "Bar chart showing top performing products",
        category: "sales",
        defaultSize: { width: 6, height: 4 },
        icon: "mdi-chart-bar",
        configSchema: {
            title: { type: "string", default: "Top Products" },
            chartType: { type: "string", default: "bar" },
            limit: { type: "number", default: 10 },
        },
        dataEndpoint: "/api/charts/top-products",
    },
    // Tables
    {
        id: "table_recent_orders",
        type: "table",
        name: "Recent Orders",
        description: "Table showing recent orders",
        category: "sales",
        defaultSize: { width: 8, height: 4 },
        icon: "mdi-table",
        configSchema: {
            title: { type: "string", default: "Recent Orders" },
            limit: { type: "number", default: 10 },
        },
        dataEndpoint: "/api/tables/recent-orders",
    },
    {
        id: "table_user_activity",
        type: "table",
        name: "User Activity",
        description: "Table showing user activity",
        category: "users",
        defaultSize: { width: 8, height: 4 },
        icon: "mdi-table",
        configSchema: {
            title: { type: "string", default: "User Activity" },
            limit: { type: "number", default: 10 },
        },
        dataEndpoint: "/api/tables/user-activity",
    },
];

// Work Orders Firebase helpers
export const createWorkOrder = async(workOrderData) => {
    try {
        const workOrderId = `wo_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
        const workOrder = {
            id: workOrderId,
            workOrderNumber: `WO-${Math.floor(100000 + Math.random() * 900000)}`,
            createdAt: new Date().toISOString(),
            status: "Open",
            priority: "High",
            ...workOrderData,
        };

        const response = await axios.put(
            `${firebaseUrl}/work_orders/${workOrderId}.json?auth=${firebaseApiKey}`,
            workOrder
        );

        return { workOrderId, ...response.data };
    } catch (error) {
        console.error("Error creating work order:", error);
        throw error;
    }
};

export const getWorkOrders = async() => {
    try {
        const response = await axios.get(
            `${firebaseUrl}/work_orders.json?auth=${firebaseApiKey}`
        );

        if (!response.data) {
            return [];
        }

        return Object.keys(response.data).map((key) => ({
            key,
            ...response.data[key],
        }));
    } catch (error) {
        console.warn("Error fetching work orders:", error);
        return [];
    }
};

export const initializeWorkOrders = async() => {
    try {
        const response = await axios.get(
            `${firebaseUrl}/work_orders.json?auth=${firebaseApiKey}`
        );

        if (response.data && Object.keys(response.data).length > 0) {
            return;
        }

        const dummyWorkOrder = {
            id: "wo_dummy_001",
            workOrderNumber: "WO-123456",
            title: "Critical Fastener Inspection",
            description: "Inspect and tighten critical fasteners showing loosening alerts",
            deviceId: "ximu3-001",
            locationId: "location_001",
            alertId: "alert_001",
            createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            status: "Completed",
            priority: "High",
            assignedTo: "John Smith",
            completedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
            estimatedHours: 2,
            actualHours: 1.5,
        };

        await axios.put(
            `${firebaseUrl}/work_orders/wo_dummy_001.json?auth=${firebaseApiKey}`,
            dummyWorkOrder
        );
    } catch (error) {
        console.warn("Failed to initialize work orders:", error);
    }
};

// Inspections Firebase helpers
export const createInspection = async(inspectionData) => {
    try {
        const inspectionId = `insp_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
        const inspection = {
            id: inspectionId,
            inspectionNumber: `INSP-${Math.floor(100000 + Math.random() * 900000)}`,
            createdAt: new Date().toISOString(),
            status: "Scheduled",
            ...inspectionData,
        };

        const response = await axios.put(
            `${firebaseUrl}/inspections/${inspectionId}.json?auth=${firebaseApiKey}`,
            inspection
        );

        console.log(
            `Inspection ${inspectionId} successfully created:`,
            response.data
        );
        return { inspectionId, ...response.data };
    } catch (error) {
        console.error("Error creating inspection:", error);
        throw error;
    }
};

export const getInspections = async() => {
    try {
        const response = await axios.get(
            `${firebaseUrl}/inspections.json?auth=${firebaseApiKey}`
        );

        if (!response.data) {
            return [];
        }

        return Object.keys(response.data).map((key) => ({
            key,
            ...response.data[key],
        }));
    } catch (error) {
        console.warn("Error fetching inspections:", error);
        return [];
    }
};

export const initializeInspections = async() => {
    try {
        const response = await axios.get(
            `${firebaseUrl}/inspections.json?auth=${firebaseApiKey}`
        );

        if (response.data && Object.keys(response.data).length > 0) {
            return;
        }

        const dummyInspection = {
            id: "insp_dummy_001",
            inspectionNumber: "INSP-789012",
            title: "Routine Fastener Inspection",
            description: "Scheduled routine inspection of critical fasteners",
            deviceId: "ximu3-002",
            locationId: "location_002",
            scheduledDate: new Date(
                Date.now() + 3 * 24 * 60 * 60 * 1000
            ).toISOString(),
            scheduledTime: "14:00",
            createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            status: "Scheduled",
            inspector: "Jane Doe",
            estimatedDuration: 1.5,
            notes: "Focus on high-stress connection points",
        };

        await axios.put(
            `${firebaseUrl}/inspections/insp_dummy_001.json?auth=${firebaseApiKey}`,
            dummyInspection
        );
    } catch (error) {
        console.warn("Failed to initialize inspections:", error);
    }
};

// Dashboard Templates Functions
export const saveDashboardAsTemplate = async(
    dashboardId,
    templateName,
    templateDescription
) => {
    try {
        // Get the dashboard data
        const response = await axios.get(
            `${firebaseUrl}/user_dashboards/${dashboardId}.json?auth=${firebaseApiKey}`
        );

        if (!response.data) {
            throw new Error("Dashboard not found");
        }

        const dashboard = response.data;

        // Create template data (remove user-specific info)
        const template = {
            templateId: `template_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`,
            name: templateName || dashboard.name,
            description: templateDescription || dashboard.description,
            layout: dashboard.layout,
            widgets: dashboard.widgets,
            createdAt: new Date().toISOString(),
            isDefault: false,
        };

        // Save template to Firebase
        const templateResponse = await axios.put(
            `${firebaseUrl}/dashboard_templates/${template.templateId}.json?auth=${firebaseApiKey}`,
            template
        );

        console.log(
            `Dashboard template ${template.templateId} successfully created:`,
            templateResponse.data
        );
        return { templateId: template.templateId, ...templateResponse.data };
    } catch (error) {
        console.error("Error saving dashboard as template:", error);
        throw error;
    }
};

export const getDashboardTemplates = async() => {
    try {
        const response = await axios.get(
            `${firebaseUrl}/dashboard_templates.json?auth=${firebaseApiKey}`
        );

        if (!response.data) {
            return [];
        }

        return Object.keys(response.data).map((key) => ({
            key,
            ...response.data[key],
        }));
    } catch (error) {
        console.warn("Error fetching dashboard templates:", error);
        return [];
    }
};

export const createDashboardFromTemplate = async(userId, templateId) => {
    try {
        // Get the template
        const response = await axios.get(
            `${firebaseUrl}/dashboard_templates/${templateId}.json?auth=${firebaseApiKey}`
        );

        if (!response.data) {
            throw new Error("Template not found");
        }

        const template = response.data;

        // Create dashboard data from template
        const dashboardData = {
            name: template.name,
            description: template.description,
            layout: template.layout,
            widgets: template.widgets,
        };

        // Create the dashboard for the user
        return await createUserDashboard(userId, dashboardData);
    } catch (error) {
        console.error("Error creating dashboard from template:", error);
        throw error;
    }
};

export const initializeDefaultDashboardTemplates = async() => {
    try {
        const deviceMonitoringTemplate = {
            templateId: "template_device_monitoring",
            name: "Device Monitoring",
            description: "Monitor device status, battery levels, and alerts across your IoT infrastructure",
            layout: { columns: 12, gap: 16 },
            widgets: [{
                    id: "widget_device_status",
                    type: "chart",
                    templateId: "metric_device_status",
                    position: { x: 0, y: 0, width: 4, height: 3 },
                    title: "Device Status",
                    config: { title: "Device Status", chartType: "doughnut" },
                    dataSource: "/api/charts/device-status",
                },
                {
                    id: "widget_battery_levels",
                    type: "chart",
                    templateId: "metric_battery_levels",
                    position: { x: 4, y: 0, width: 8, height: 3 },
                    title: "Device Battery Levels",
                    config: { title: "Device Battery Levels", chartType: "bar" },
                    dataSource: "/api/charts/battery-levels",
                },
                {
                    id: "widget_alerts_over_time",
                    type: "chart",
                    templateId: "metric_alerts_over_time",
                    position: { x: 0, y: 3, width: 6, height: 4 },
                    title: "Alerts Over Time",
                    config: {
                        title: "Alerts Over Time",
                        chartType: "line",
                        timeRange: "30d",
                    },
                    dataSource: "/api/charts/alerts-timeline",
                },
                {
                    id: "widget_alerts_by_location",
                    type: "chart",
                    templateId: "metric_alerts_by_location",
                    position: { x: 6, y: 3, width: 6, height: 4 },
                    title: "Alerts by Location",
                    config: { title: "Alerts by Location", chartType: "bar" },
                    dataSource: "/api/charts/alerts-by-location",
                },
            ],
            createdAt: new Date().toISOString(),
            isDefault: true,
        };

        const sensorAnalyticsTemplate = {
            templateId: "template_sensor_analytics",
            name: "Sensor Analytics",
            description: "Analyze sensor data including torque measurements and device health monitoring",
            layout: { columns: 12, gap: 16 },
            widgets: [{
                    id: "widget_alerts_over_time",
                    type: "chart",
                    templateId: "metric_alerts_over_time",
                    position: { x: 0, y: 0, width: 12, height: 4 },
                    title: "Alerts Over Time",
                    config: {
                        title: "Alerts Over Time",
                        chartType: "line",
                        timeRange: "30d",
                    },
                    dataSource: "/api/charts/alerts-timeline",
                },
                {
                    id: "widget_torque_deviation",
                    type: "chart",
                    templateId: "metric_torque_deviation",
                    position: { x: 0, y: 4, width: 8, height: 4 },
                    title: "Torque Deviation Analysis",
                    config: { title: "Torque Analysis", chartType: "scatter" },
                    dataSource: "/api/charts/torque-deviation",
                },
                {
                    id: "widget_device_health",
                    type: "metric",
                    templateId: "metric_device_health_score",
                    position: { x: 8, y: 4, width: 4, height: 2 },
                    title: "Device Health Score",
                    config: { title: "Device Health", color: "#4CAF50" },
                    dataSource: "/api/metrics/device-health",
                },
                {
                    id: "widget_critical_alerts",
                    type: "metric",
                    templateId: "metric_critical_alerts",
                    position: { x: 8, y: 6, width: 4, height: 2 },
                    title: "Critical Alerts",
                    config: { title: "Critical Alerts", color: "#f44336" },
                    dataSource: "/api/metrics/critical-alerts",
                },
            ],
            createdAt: new Date().toISOString(),
            isDefault: true,
        };

        const maintenanceTemplate = {
            templateId: "template_maintenance_ops",
            name: "Maintenance Dashboard",
            description: "Track work orders, maintenance schedules, and asset lifecycle management",
            layout: { columns: 12, gap: 16 },
            widgets: [{
                    id: "widget_work_orders",
                    type: "chart",
                    templateId: "metric_work_orders",
                    position: { x: 0, y: 0, width: 12, height: 4 },
                    title: "Work Orders Over Time",
                    config: {
                        title: "Work Orders Over Time",
                        chartType: "line",
                        timeRange: "30d",
                    },
                    dataSource: "/api/charts/work-orders-timeline",
                },
                {
                    id: "widget_maintenance_schedule",
                    type: "table",
                    templateId: "metric_maintenance_schedule",
                    position: { x: 0, y: 4, width: 6, height: 4 },
                    title: "Maintenance Schedule",
                    config: { title: "Maintenance Schedule", limit: 10 },
                    dataSource: "/api/tables/maintenance-schedule",
                },
                {
                    id: "widget_asset_lifecycle",
                    type: "table",
                    templateId: "metric_asset_lifecycle",
                    position: { x: 6, y: 4, width: 6, height: 4 },
                    title: "Asset Lifecycle",
                    config: { title: "Asset Lifecycle", limit: 10 },
                    dataSource: "/api/tables/asset-lifecycle",
                },
            ],
            createdAt: new Date().toISOString(),
            isDefault: true,
        };

        const templates = [
            deviceMonitoringTemplate,
            sensorAnalyticsTemplate,
            maintenanceTemplate,
        ];

        for (const template of templates) {
            try {
                await saveDashboardTemplate(template);
            } catch (error) {
                console.error(
                    `Error initializing template ${template.name}:`,
                    error
                );
            }
        }

        return templates;
    } catch (error) {
        console.error("Error initializing default dashboard templates:", error);
        throw error;
    }
};

export const createDefaultDashboardsForUser = async(userId) => {
    try {
        const templates = await getDashboardTemplates();
        const defaultTemplates = templates.filter((template) => template.isDefault);

        if (defaultTemplates.length === 0) {
            await initializeDefaultDashboardTemplates();
            const newTemplates = await getDashboardTemplates();
            const newDefaultTemplates = newTemplates.filter(
                (template) => template.isDefault
            );

            if (newDefaultTemplates.length === 0) {
                console.warn("Still no default templates after initialization");
                return [];
            }

            for (const template of newDefaultTemplates) {
                await createDashboardFromTemplate(userId, template);
            }

            return newDefaultTemplates;
        }

        const createdDashboards = [];
        for (const template of defaultTemplates) {
            try {
                const dashboard = await createDashboardFromTemplate(userId, template);
                createdDashboards.push(dashboard);
            } catch (error) {
                console.error(
                    `Failed to create dashboard "${template.name}" for user ${userId}:`,
                    error
                );
            }
        }

        return createdDashboards;
    } catch (error) {
        console.error("Error creating default dashboards for user:", error);
        throw error;
    }
};